// @flow

import React, { useRef, useState } from "react"
import { withRouter } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { injectIntl } from "react-intl"
import OrderSearchStore from "shared/stores/OrderSearchStore"
import { Button, Col, Icon, Modal, notification, Row, Tooltip } from "antd"
import styled from "styled-components"
import { PrimaryButton } from "@cabonline/shared/components"
import { get, isEmpty, isEqual, isNull, lowerCase, replace } from "lodash"
import type { Address, VehicleOnMapDetails } from "shared/types/OrdersType"
import { TRIP_STATUS } from "shared/types/OrdersType"
import paths from "shared/utils/paths"
import OrderSearchUtils from "apps/orders/OrderSearchUtils"
import { toTitleCase } from "shared/utils/stringUtils"
import type { Coords } from "shared/types/Coords"
import TrackedMap from "../../shared/components/maps/TrackedMap/TrackedMap"
import {
  onCopyLinkClicked,
  copyToClipboard
} from "shared/utils/copyToClipBoard"
import WideSpinner from "shared/components/WideSpinner"
import { messageError } from "shared/utils/errors"
// Styles
import styles from "./OrderSummaryView.scss"
import DefaultButton from "@cabonline/shared/components/buttons/DefaultButton"
import SummaryOptionsModal from "apps/orders/SummaryOptionsModal"
import moment from "moment"

const Container = styled.div`
  margin-left: 20px;
  border-radius: 6px;
  box-shadow: 0 2px 16px 0 #ededed;
  background-color: #ffffff;
  overflow-y: auto;
  max-height: calc(100vh - 100px);
`

const HeaderSeparator = styled.div`
  height: 1px;
  width: 100%;
  background-color: #ececec;
`

const BodySeparatorContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
`

const BodySeparator = styled.div`
  height: 1px;
  width: 100%;
  background-color: #ececec;
`

const OrderNotSelectedDiv = styled.div``

const SelectedOrderDiv = styled.div`
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  align-items: center;
`

const PublicIdDiv = styled.div`
  font-size: 20px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  width: 100%;
  text-align: left;
  margin-bottom: 5px;
  cursor: pointer;
`
const FrogneIdRow = styled(Row)`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #979797;
  width: 100%;
  text-align: left;
  padding-top: 1px;
  padding-bottom: 1px;
  cursor: pointer;
`

const LocationItem = styled.div`
  margin-top: 10px;
  margin-bottom: 10px;
`

const LocationLabelRow = styled(Row)`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #989898;
`

const LocationValueRow = styled(Row)`
  font-size: 15px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
`

const MapSubTitleRow = styled(Row)`
  font-size: 15px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
`

const DetailContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  margin-top: 10px;
  margin-bottom: 10px;
`

const DetailText = styled.div`
  font-size: 15px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
  margin-left: 5px;
`

const SourceDiv = styled.div`
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #989898;
  margin-left: 5px;
`

const SummaryHeaderDiv = styled(Row)`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 10px;
`

const SummaryContentDiv = styled.div`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
`

const SummaryFooterDiv = styled.div`
  padding: 10px;
  width: 100%;
`

const ModalContainer = styled.div`
  position: relative;
`

const HeaderContentDiv = styled.div`
  width: 100%;
`

const BodyContentDiv = styled.div`
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-start;
  width: 100%;
`

const FooterContentDiv = styled.div`
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`

const SummaryHeaderButtons = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
`

const MapLink = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #0080ff;
  margin-left: 5px;
`

const OrderStatusSectionWrapper = styled.div`
  display: flex;
  flex-flow: column wrap;
`

const getCoordinates = (address: Address): ?Coords => {
  const latitude = get(address, "latitude")
  const longitude = get(address, "longitude")

  if (!latitude || !longitude) return

  return {
    lat: latitude,
    lng: longitude
  }
}

const getVehiclePosition = (vehicleDetails: VehicleOnMapDetails) => {
  const latitude = get(vehicleDetails.position, "latitude")
  const longitude = get(vehicleDetails.position, "longitude")

  if (!latitude || !longitude) return

  return {
    lat: latitude,
    lng: longitude
  }
}

const renderTravelerDetails = (name, phone) => (
  <DetailContainer>
    <Icon type="user" style={{ fontSize: "20px", marginRight: "5px" }} />
    <div style={{ display: "flex", flexFlow: "column" }}>
      <DetailText>{toTitleCase(name)}</DetailText>
      <div>{phone}</div>
    </div>
  </DetailContainer>
)

const renderPaymentDetails = (amount, currency) => (
  <DetailContainer>
    <Icon type="credit-card" style={{ fontSize: "20px", marginRight: "5px" }} />
    <DetailText>
      {amount ? (
        <SourceDiv>
          {amount} {currency}
        </SourceDiv>
      ) : (
        <p>-</p>
      )}
    </DetailText>
  </DetailContainer>
)

const originDetails = (createdAt, source, appVersion, brand) => (
  <DetailContainer>
    <Icon
      type="exclamation-circle"
      style={{ fontSize: "20px", marginRight: "5px" }}
    />
    <div style={{ display: "flex", flexFlow: "column" }}>
      <DetailText>Created {createdAt}</DetailText>
      <SourceDiv>
        Via {appVersion} {toTitleCase(brand)} {source}
      </SourceDiv>
    </div>
  </DetailContainer>
)

const OrderSummaryView = props => {
  const {
    history,
    intl: { formatMessage }
  } = props

  const { selectedOrder } = OrderSearchStore
  const optionModalRef = useRef()

  const renderEmptyOrderSummary = () => {
    return (
      <OrderNotSelectedDiv>
        <Row>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Icon
              type="exclamation-circle"
              style={{ fontSize: "60px", opacity: "0.5", marginTop: "80px" }}
            />
          </div>
        </Row>
        <Row>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "30px",
              paddingLeft: "10px",
              paddingRight: "10px"
            }}
          >
            <p style={{ width: "100%", textAlign: "center" }}>
              {formatMessage({ id: "apps.order-search.order-summary.empty" })}
            </p>
          </div>
        </Row>
      </OrderNotSelectedDiv>
    )
  }

  const RenderSummaryContent = () => {
    const [isModalOpen, setModalOpen] = useState(false)
    const [isMapLoading, setMapLoading] = useState(false)
    const customerPrice = get(selectedOrder, "customerPrice", "-")
    return (
      <SummaryContentDiv>
        {renderLocationDetails()}
        <BodySeparatorContainer>
          <BodySeparator />
        </BodySeparatorContainer>
        {renderVehicleDetails(
          get(selectedOrder, "state"),
          formatMessage({
            id:
              "apps.order-search.state." +
              lowerCase(replace(get(selectedOrder, "state"), /_/g, ""))
          }),
          setModalOpen,
          isModalOpen,
          setMapLoading,
          isMapLoading
        )}
        <BodySeparatorContainer>
          <BodySeparator />
        </BodySeparatorContainer>
        {renderTravelerDetails(
          get(selectedOrder, "travelerName"),
          get(selectedOrder, "travelerPhoneNumber")
        )}
        <BodySeparatorContainer>
          <BodySeparator />
        </BodySeparatorContainer>
        {renderPaymentDetails(
          !isNull(customerPrice) ? customerPrice : "-",
          get(selectedOrder, "currencyCode", "-")
        )}
        <BodySeparatorContainer>
          <BodySeparator />
        </BodySeparatorContainer>
        {originDetails(
          get(selectedOrder, "createdAt"),
          get(selectedOrder, "source"),
          get(selectedOrder, "appVersion"),
          get(selectedOrder, "brand")
        )}
      </SummaryContentDiv>
    )
  }

  const renderLocationDetails = () => {
    const viaAddress = get(selectedOrder, "viaAddress", undefined)
    const pickupTime = get(selectedOrder, "pickupTime", "-")

    return (
      <div>
        <LocationItem>
          <LocationLabelRow>
            {formatMessage({
              id: "apps.order-search.label.from"
            })}
          </LocationLabelRow>
          <LocationValueRow>
            {OrderSearchUtils.addressObjToText(selectedOrder.fromAddress)}
          </LocationValueRow>
        </LocationItem>
        {viaAddress && viaAddress.length > 0 && (
          <LocationItem>
            <LocationLabelRow>
              {formatMessage({
                id: "apps.order-search.label.via"
              })}
            </LocationLabelRow>
            <LocationItem>
              {viaAddress.map((address: Address, index: number) => (
                <LocationValueRow key={index}>
                  {OrderSearchUtils.addressObjToText(address)}
                </LocationValueRow>
              ))}
            </LocationItem>
          </LocationItem>
        )}
        <LocationItem>
          <LocationLabelRow>
            {formatMessage({
              id: "apps.order-search.label.to"
            })}
          </LocationLabelRow>
          <LocationValueRow>
            {OrderSearchUtils.addressObjToText(selectedOrder.toAddress)}
          </LocationValueRow>
        </LocationItem>
        <LocationItem>
          <LocationLabelRow>
            {formatMessage({
              id: "apps.order-search.search-result.pickup-time"
            })}
          </LocationLabelRow>
          <LocationValueRow>
            {get(selectedOrder, "travelNow", false)
              ? pickupTime
              : `${pickupTime} (${formatMessage({
                  id: "apps.order-search.label.prebooked"
                })})`}
          </LocationValueRow>
        </LocationItem>
      </div>
    )
  }

  const IdCopiedMessage = formatMessage({
    id: "apps.order-search.message.idCopiedToClipboard"
  })

  const onCopyPublicIdClicked = () => {
    copyToClipboard(get(selectedOrder, "publicId", ""))
    notification.success({
      message: IdCopiedMessage
    })
  }

  const onCopyFrogneIdClicked = (item: any) => {
    copyToClipboard(item.id)
    notification.success({
      message: IdCopiedMessage
    })
  }

  const onViewOrderClicked = () => {
    if (window.location.href.includes("iframe"))
      history.push(paths.orders.iframeDetailOrder(selectedOrder.orderId))
    else history.push(paths.orders.detailOrder(selectedOrder.orderId))
  }

  const onOptionsClicked = () => {
    if (optionModalRef.current) optionModalRef.current.showModal()
  }

  const updateSelectedOrderToCancelled = (updatedOrder: any) => {
    OrderSearchStore.setSelectedOrder(updatedOrder)
  }

  const updateOrderInListToCancelled = (updatedOrder: any) => {
    const orders = OrderSearchStore.orders
    OrderSearchStore.setOrders(
      orders.map(o =>
        o.orderId === selectedOrder.orderId
          ? Object.assign({}, o, updatedOrder)
          : o
      )
    )
  }

  const onCancelOrderClicked = async () => {
    props.addPendingRequestToQueue()
    await OrderSearchStore.cancelOrder(get(selectedOrder, "orderId"))
      .then(() => {
        const updatedOrder = {
          ...selectedOrder,
          state: TRIP_STATUS.CANCELLED,
          status: "HANDLED",
          cancellable: false,
          modifiable: false
        }
        updateSelectedOrderToCancelled(updatedOrder)
        updateOrderInListToCancelled(updatedOrder)
        props.reducePendingRequestFromQueue()
      })
      .catch(e => {
        messageError("Failed to cancel order", e)
        props.reducePendingRequestFromQueue()
      })
  }

  const orderDetailsPath =
    window.location.origin + paths.orders.detailOrder(selectedOrder.orderId)

  const onOpenNewTabClicked = () => {
    window.open(orderDetailsPath, "_blank")
  }

  const clickToCopyIdToolTip = (
    <span>
      {formatMessage({ id: "apps.order-search.label.clickToCopyId" })}
    </span>
  )

  const renderSelectedNonEmptyOrder = () => {
    return (
      <SelectedOrderDiv>
        <HeaderContentDiv>
          {
            <SummaryHeaderDiv>
              <Col span={16}>
                <Tooltip placement="rightBottom" title={clickToCopyIdToolTip}>
                  <PublicIdDiv
                    id={get(selectedOrder, "publicId")}
                    onClick={onCopyPublicIdClicked}
                  >
                    Order {get(selectedOrder, "publicId")}
                  </PublicIdDiv>
                </Tooltip>
                {get(selectedOrder, "externalReferences").map((item, index) => {
                  if (!OrderSearchUtils.isOrderReference(item.provider)) {
                    return
                  }
                  return (
                    <FrogneIdRow
                      key={index}
                      onClick={() => onCopyFrogneIdClicked(item)}
                    >
                      <Tooltip
                        placement="rightBottom"
                        title={clickToCopyIdToolTip}
                      >
                        {OrderSearchUtils.resolveExternalRefPrettyName(
                          item.provider
                        )}{" "}
                        - {item.id}
                      </Tooltip>
                    </FrogneIdRow>
                  )
                })}
              </Col>
              <Col span={8}>
                <SummaryHeaderButtons>
                  <Tooltip
                    placement="rightBottom"
                    title={formatMessage({
                      id: "apps.orders.tooltip.detailPageLink"
                    })}
                  >
                    <Button
                      data-cy="cy_toolTipCopyLink"
                      type="primary"
                      shape="circle"
                      icon="link"
                      className={styles.copylinkbutton}
                      size="small"
                      onClick={() =>
                        onCopyLinkClicked(
                          formatMessage({
                            id:
                              "apps.order-search.message.detailUrlCopiedToClipboard"
                          }),
                          orderDetailsPath
                        )
                      }
                    />
                  </Tooltip>
                  <Tooltip
                    placement="rightBottom"
                    title={formatMessage({
                      id: "apps.orders.tooltip.openDetailPageNewTab"
                    })}
                  >
                    <Button
                      data-cy="cy_toolTipOpenOrderInNewTab"
                      type="primary"
                      shape="circle"
                      icon="select"
                      className={styles.openintabbutton}
                      size="small"
                      onClick={onOpenNewTabClicked}
                    />
                  </Tooltip>
                </SummaryHeaderButtons>
              </Col>
            </SummaryHeaderDiv>
          }
        </HeaderContentDiv>
        <HeaderSeparator />
        <BodyContentDiv>
          <RenderSummaryContent />
        </BodyContentDiv>
        <HeaderSeparator />
        <FooterContentDiv>
          <ModalContainer>
            <SummaryFooterDiv>
              <PrimaryButton
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  marginBottom: "10px"
                }}
                onClick={onViewOrderClicked}
              >
                {formatMessage({ id: "apps.order-search.button.viewOrder" })}
              </PrimaryButton>
              <DefaultButton
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  backgroundColor: "#f3f3f3"
                }}
                onClick={onOptionsClicked}
              >
                {formatMessage({ id: "common.options" })}
              </DefaultButton>
              <SummaryOptionsModal
                cancelButtonText={formatMessage({
                  id: "apps.orders.order-search.cancelOrder"
                })}
                headingText={formatMessage({ id: "common.options" })}
                ref={optionModalRef}
                onCancelButtonClick={onCancelOrderClicked}
                disableCancelButton={!get(selectedOrder, "cancellable", true)}
              />
            </SummaryFooterDiv>
          </ModalContainer>
        </FooterContentDiv>
      </SelectedOrderDiv>
    )
  }

  const renderSelectedOrderContent = () => {
    if (isEmpty(selectedOrder)) {
      return renderEmptyOrderSummary()
    } else {
      return renderSelectedNonEmptyOrder()
    }
  }

  const renderVehicleDetails = (
    state,
    prettyName,
    setModalOpen,
    isModalOpen,
    setMapLoading,
    isMapLoading
  ) => {
    const { selectedOrder } = OrderSearchStore
    const { vehicleOnMapDetails } = OrderSearchStore
    const fromCoordinates = getCoordinates(
      get(selectedOrder, "fromAddress", {})
    )
    const toCoordinates = getCoordinates(get(selectedOrder, "toAddress", {}))
    const viaCoordinates = get(selectedOrder, "viaAddress", []).map(a =>
      getCoordinates(a)
    )

    const departureTime = selectedOrder.pickupTime

    async function calculateETA() {
      setMapLoading(true)
      try {
        await OrderSearchStore.findVehicleOnMap(
          get(selectedOrder, "orderId"),
          fromCoordinates ? fromCoordinates.lat.toString() : "0.0",
          fromCoordinates ? fromCoordinates.lng.toString() : "0.0"
        )
        setMapLoading(false)
      } catch (e) {
        setMapLoading(false)
        messageError("order-search", e)
      }
    }

    function showETAonMap() {
      calculateETA()
      setModalOpen(true)
    }

    return (
      <DetailContainer>
        <img
          src={OrderSearchUtils.renderStatusIcon(state)}
          width={20}
          style={{ marginRight: "5px" }}
          alt="StatusIcon"
        />
        <OrderStatusSectionWrapper data-cy="cy_orderStatus">
          <DetailText>{prettyName}</DetailText>
          {isEqual(state, TRIP_STATUS.EN_ROUTE) ? (
            <div>
              <MapLink onClick={() => showETAonMap()}>
                {formatMessage({
                  id: "apps.order-search.map.en-route.link"
                })}
              </MapLink>
              <Modal
                visible={isModalOpen}
                title={renderMapTitle(
                  get(selectedOrder, "publicId"),
                  vehicleOnMapDetails.secondsToCustomer,
                  prettyName
                )}
                footer={null}
                onCancel={() => setModalOpen(false)}
                maskClosable={true}
                width={807}
              >
                <div style={{ height: 480, width: "100%" }}>
                  {isMapLoading ? (
                    <WideSpinner />
                  ) : (
                    <TrackedMap
                      from={fromCoordinates}
                      to={toCoordinates}
                      departureTime={
                        departureTime && moment(departureTime).toISOString(true)
                      }
                      vehiclePosition={getVehiclePosition(vehicleOnMapDetails)}
                      locked={false}
                      via={viaCoordinates}
                      isAnimatedPath={false}
                    />
                  )}
                </div>
              </Modal>
            </div>
          ) : null}
        </OrderStatusSectionWrapper>
      </DetailContainer>
    )
  }

  const renderMapTitle = (publicId, eta, tripStatus) => {
    let title

    if (eta < 30) {
      title = formatMessage({ id: "apps.order-search.map.eta.title-30sec" })
    } else if (eta < 60) {
      title = formatMessage({ id: "apps.order-search.map.eta.title-60sec" })
    } else {
      const etaMinutes = isNaN(eta) ? " " : Math.ceil(eta / 60)
      const etaMessage = formatMessage({
        id: "apps.order-search.map.eta.title"
      })
      title = `${tripStatus} - ${etaMinutes} ${etaMessage}`
    }

    return (
      <div data-cy="cy_orderPublicId">
        <PublicIdDiv>Order {publicId}</PublicIdDiv>
        <MapSubTitleRow>{title}</MapSubTitleRow>
      </div>
    )
  }
  return (
    <Container data-cy="cy_orderSummaryView">
      {renderSelectedOrderContent()}
    </Container>
  )
}

export default (injectIntl(withRouter(observer(OrderSummaryView))): any)
